import { mountImageBatch } from './image-batch-queue.js';
import { canvasToBlob, imageIsAr, imageName, makeCanvas, nextFrame, openBitmap } from './image-batch-utils.js';
import { trackEvent } from '../core/analytics.js';

const t = (ar, en) => imageIsAr ? ar : en;
const fields = [
  {
    key: 'mode',
    label: t('نوع الهوامش', 'Margin type'),
    type: 'select',
    default: 'auto',
    options: [
      { value: 'auto', label: t('تلقائي (شفافية أو لون موحد)', 'Auto (Transparent or Solid)') },
      { value: 'alpha', label: t('شفافية فقط (Alpha)', 'Transparent only (Alpha)') },
      { value: 'color', label: t('لون الخلفية (أبيض / صلب)', 'Solid Background (White / Color)') }
    ]
  },
  { key: 'threshold', label: t('حساسية القص', 'Trim sensitivity'), type: 'range', min: 0, max: 90, step: 1, default: 5, suffix: '%' },
  { key: 'padding', label: t('الهامش المحيط', 'Padding'), type: 'number', min: 0, max: 200, step: 1, default: 0, suffix: 'px' }
];

function getCachedPixels(bitmap, item) {
  if (item._cachedPixels && item._cachedBitmap === bitmap) {
    return item._cachedPixels;
  }
  const w = bitmap.width;
  const h = bitmap.height;
  const scanCanvas = document.createElement('canvas');
  scanCanvas.width = w;
  scanCanvas.height = h;
  const scanCtx = scanCanvas.getContext('2d', { willReadFrequently: true });
  scanCtx.drawImage(bitmap, 0, 0);
  let pixels = null;
  try {
    pixels = scanCtx.getImageData(0, 0, w, h).data;
  } catch {
    pixels = null;
  }
  item._cachedPixels = pixels;
  item._cachedBitmap = bitmap;
  return pixels;
}

function computeTrimBounds(pixels, w, h, thresholdPercent, paddingPx, mode = 'auto') {
  if (!pixels || w <= 0 || h <= 0) return null;
  const threshNum = (thresholdPercent === undefined || thresholdPercent === null || thresholdPercent === '') ? 5 : Number(thresholdPercent);
  const cutoff = Math.round(Math.max(0, Math.min(100, threshNum)) * 2.55);
  const data32 = new Uint32Array(pixels.buffer, pixels.byteOffset, w * h);

  // Check 4 corner pixels for transparency or solid background color
  const tlIdx = 0;
  const trIdx = w - 1;
  const blIdx = (h - 1) * w;
  const brIdx = (h - 1) * w + (w - 1);

  const tlA = data32[tlIdx] >>> 24;
  const trA = data32[trIdx] >>> 24;
  const blA = data32[blIdx] >>> 24;
  const brA = data32[brIdx] >>> 24;

  const hasTransparentCorner = tlA <= cutoff || trA <= cutoff || blA <= cutoff || brA <= cutoff;

  let useColorTrim = false;
  let bgR = 255, bgG = 255, bgB = 255;
  const colorTolerance = Math.round(threshNum * 2.5) + 15;

  if (mode === 'color') {
    useColorTrim = true;
    const c = data32[tlIdx];
    bgR = c & 0xff;
    bgG = (c >>> 8) & 0xff;
    bgB = (c >>> 16) & 0xff;
  } else if (mode === 'alpha') {
    useColorTrim = false;
  } else {
    // 'auto' mode: if all 4 corners are fully opaque, check for uniform background color (like white)
    if (!hasTransparentCorner) {
      const c = data32[tlIdx];
      bgR = c & 0xff;
      bgG = (c >>> 8) & 0xff;
      bgB = (c >>> 16) & 0xff;
      useColorTrim = true;
    }
  }

  const isContent = useColorTrim
    ? (offset) => {
        const c = data32[offset];
        const a = c >>> 24;
        if (a <= cutoff) return false;
        const r = c & 0xff;
        const g = (c >>> 8) & 0xff;
        const b = (c >>> 16) & 0xff;
        return (Math.abs(r - bgR) + Math.abs(g - bgG) + Math.abs(b - bgB)) > colorTolerance;
      }
    : (offset) => (data32[offset] >>> 24) > cutoff;

  // 1. Scan from top to find minY
  let minY = -1;
  for (let y = 0; y < h; y++) {
    const rowOffset = y * w;
    for (let x = 0; x < w; x++) {
      if (isContent(rowOffset + x)) {
        minY = y;
        break;
      }
    }
    if (minY !== -1) break;
  }

  if (minY === -1) return null; // Fully transparent or uniform background

  // 2. Scan from bottom to find maxY
  let maxY = minY;
  for (let y = h - 1; y >= minY; y--) {
    const rowOffset = y * w;
    for (let x = 0; x < w; x++) {
      if (isContent(rowOffset + x)) {
        maxY = y;
        break;
      }
    }
    if (maxY !== minY) break;
  }

  // 3. Scan columns between minY and maxY to find minX and maxX
  let minX = w;
  let maxX = -1;
  for (let y = minY; y <= maxY; y++) {
    const rowOffset = y * w;
    for (let x = 0; x < minX; x++) {
      if (isContent(rowOffset + x)) {
        minX = x;
        break;
      }
    }
    for (let x = w - 1; x > maxX; x--) {
      if (isContent(rowOffset + x)) {
        maxX = x;
        break;
      }
    }
  }

  if (maxX < 0 || minX > maxX) return null;

  const pad = Math.max(0, Math.min(200, Number(paddingPx) || 0));
  const finalMinX = Math.max(0, minX - pad);
  const finalMinY = Math.max(0, minY - pad);
  const finalMaxX = Math.min(w - 1, maxX + pad);
  const finalMaxY = Math.min(h - 1, maxY + pad);

  return {
    minX: finalMinX,
    minY: finalMinY,
    maxX: finalMaxX,
    maxY: finalMaxY,
    width: finalMaxX - finalMinX + 1,
    height: finalMaxY - finalMinY + 1,
    isColorTrim: useColorTrim
  };
}

mountImageBatch({
  rootId: 'trim-app',
  accept: 'image/png',
  fields,
  zipName: 'trimmed-png-images.zip',
  renderPreview: ({ canvas, ctx, bitmap, settings, item, statsEl, isAr }) => {
    const w = bitmap.width;
    const h = bitmap.height;
    const containerW = canvas.parentElement?.clientWidth || 720;
    const maxStageW = Math.min(760, containerW - 24);
    const maxStageH = 460;
    const scale = Math.min(maxStageW / w, maxStageH / h, 1);
    const displayW = Math.max(64, Math.round(w * scale));
    const displayH = Math.max(48, Math.round(h * scale));

    canvas.style.width = `${displayW}px`;
    canvas.style.height = `${displayH}px`;
    canvas.width = displayW;
    canvas.height = displayH;

    // 1. Transparency checkerboard pattern
    const checkSize = 12;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, displayW, displayH);
    ctx.fillStyle = '#f1f5f9';
    for (let cy = 0; cy < displayH; cy += checkSize) {
      for (let cx = 0; cx < displayW; cx += checkSize) {
        if ((((cx / checkSize) | 0) ^ ((cy / checkSize) | 0)) & 1) {
          ctx.fillRect(cx, cy, checkSize, checkSize);
        }
      }
    }

    // 2. Base image
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, displayW, displayH);

    // 3. Trim bounds calculation
    const pixels = getCachedPixels(bitmap, item);
    const bounds = computeTrimBounds(pixels, w, h, settings.threshold, settings.padding, settings.mode);

    if (!bounds) {
      ctx.fillStyle = 'rgba(239, 68, 68, 0.25)';
      ctx.fillRect(0, 0, displayW, displayH);
      if (statsEl) {
        statsEl.textContent = isAr
          ? `تنبيه: الصورة شفافة أو موحدة بالكامل عند الحساسية المختارة!`
          : `Warning: Image is fully transparent or uniform at selected sensitivity!`;
      }
      return;
    }

    const boxX = bounds.minX * scale;
    const boxY = bounds.minY * scale;
    const boxW = bounds.width * scale;
    const boxH = bounds.height * scale;

    // 4. Dim transparent/trimmed area that will be cropped away
    ctx.save();
    ctx.fillStyle = 'rgba(15, 23, 42, 0.45)';
    ctx.fillRect(0, 0, displayW, boxY);
    ctx.fillRect(0, boxY + boxH, displayW, displayH - (boxY + boxH));
    ctx.fillRect(0, boxY, boxX, boxH);
    ctx.fillRect(boxX + boxW, boxY, displayW - (boxX + boxW), boxH);
    ctx.restore();

    // 5. Trim border with dashed line
    ctx.save();
    ctx.strokeStyle = '#2563eb';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([5, 4]);
    ctx.strokeRect(boxX, boxY, boxW, boxH);
    ctx.setLineDash([]);

    // 6. Accent Corner Brackets
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = '#3b82f6';
    const cLen = Math.min(12, Math.max(6, Math.min(boxW, boxH) / 4));

    ctx.beginPath();
    // Top-left
    ctx.moveTo(boxX, boxY + cLen); ctx.lineTo(boxX, boxY); ctx.lineTo(boxX + cLen, boxY);
    // Top-right
    ctx.moveTo(boxX + boxW - cLen, boxY); ctx.lineTo(boxX + boxW, boxY); ctx.lineTo(boxX + boxW, boxY + cLen);
    // Bottom-left
    ctx.moveTo(boxX, boxY + boxH - cLen); ctx.lineTo(boxX, boxY + boxH); ctx.lineTo(boxX + cLen, boxY + boxH);
    // Bottom-right
    ctx.moveTo(boxX + boxW - cLen, boxY + boxH); ctx.lineTo(boxX + boxW, boxY + boxH); ctx.lineTo(boxX + boxW, boxY + boxH - cLen);
    ctx.stroke();
    ctx.restore();

    // 7. Stats badge
    if (statsEl) {
      const origArea = w * h;
      const trimArea = bounds.width * bounds.height;
      const savedPct = Math.max(0, Math.round((1 - trimArea / Math.max(1, origArea)) * 100));
      const isTight = bounds.width === w && bounds.height === h;
      const modeNote = bounds.isColorTrim ? ` [${isAr ? 'خلفية صلبة' : 'Solid BG'}]` : '';
      statsEl.textContent = isTight
        ? `${w} × ${h} px · ${isAr ? 'الصورة بالفعل بدون هوامش زائدة (0% فراغ)' : 'Image already has no empty margins'}`
        : `${w} × ${h} px → ${bounds.width} × ${bounds.height} px · ${isAr ? `توفير ${savedPct}% مساحة فارغة` : `Saved ${savedPct}% empty space`}${modeNote}`;
    }
  },
  process: async (file, settings, report) => {
    report(15, t('جارٍ قراءة الصورة وفحص الهوامش...', 'Reading image and inspecting margins...'));
    const bitmap = await openBitmap(file);
    const w = bitmap.width;
    const h = bitmap.height;
    const { canvas, context } = makeCanvas(w, h);
    context.drawImage(bitmap, 0, 0);
    bitmap.close();

    report(35, t('جارٍ فحص البكسلات ومستوى الشفافية...', 'Inspecting pixel transparency...'));
    let pixels;
    try {
      pixels = context.getImageData(0, 0, w, h).data;
    } catch {
      throw new Error(t('تعذر قراءة شفافية الصورة بسبب قيود ذاكرة المتصفح.', 'Browser memory limits prevented reading image transparency.'));
    }

    report(65, t('جارٍ حساب أبعاد الرسم الفعلي بدقة...', 'Computing true graphic bounds...'));
    await nextFrame();

    const bounds = computeTrimBounds(pixels, w, h, settings.threshold, settings.padding, settings.mode);
    if (!bounds) {
      throw new Error(t('الصورة شفافة أو بلون موحد بالكامل عند هذا الحد.', 'The image is fully transparent or uniform at this threshold.'));
    }

    const { minX, minY, width, height } = bounds;
    const padding = Math.max(0, Math.min(200, Number(settings.padding) || 0));

    report(85, t('جارٍ استخراج وتصدير الرسم المقصوص...', 'Cropping and extracting trimmed graphic...'));
    const { canvas: output, context: out } = makeCanvas(width, height);
    out.drawImage(canvas, minX, minY, width, height, 0, 0, width, height);

    report(95, t('جارٍ إنشاء ملف PNG عالي النقاء...', 'Generating high-fidelity PNG...'));
    const blob = await canvasToBlob(output, 'image/png');
    report(100, t('اكتمل القص بنجاح', 'Trim complete'));

    trackEvent('tool_completed', 'png-auto-trim', {
      original_w: w,
      original_h: h,
      trimmed_w: width,
      trimmed_h: height,
      padding
    });

    const isNoTrim = width === w && height === h;
    const statusNote = isNoTrim
      ? t('الصورة بالفعل بدون هوامش', 'No extra margins')
      : `${Math.round((1 - (width * height) / (w * h)) * 100)}% ${t('توفير', 'saved')}`;

    return {
      blob,
      filename: `${imageName(file.name)}.png`,
      details: `${w} × ${h} → ${width} × ${height} px · ${padding}px ${t('هامش', 'padding')} · ${statusNote}`
    };
  }
});
